import { CSSResultGroup, TemplateResult, html } from 'lit';
import {
  LightDomStyles,
  OutlineElement,
  SlotsController,
} from '@phase2/outline-core';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './outline-emergency-alert.css.lit';
import '../outline-button-baseline/outline-button-baseline';
import '../outline-icon-baseline/outline-icon-baseline';

/**
 * The OutlineEmergencyAlert component
 * @element outline-emergency-alert
 */
@customElement('outline-emergency-alert')
export class OutlineEmergencyAlert extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  lightDomStyles = new LightDomStyles(
    this,
    componentStyles,
    this.tagName.toLowerCase()
  );

  /**
   * Toggle alert open/close
   */
  @property({ type: Boolean, reflect: true, attribute: 'is-open' })
  isOpen = false;

  /**
   * Alert background color
   */
  @property({ type: String, reflect: true, attribute: 'background-color' })
  backgroundColor = 'brand-red-700';

  /**
   * Alert text color
   */
  @property({ type: String, reflect: true, attribute: 'text-color' })
  textColor = 'brand-white';

  /**
   * Alert icon
   */
  @property({ type: String, reflect: true, attribute: 'icon' })
  icon = 'eu-alert';

  slots = new SlotsController(this);

  updated() {
    this.style.setProperty('--alert-text-color', `var(--${this.textColor})`);
  }

  // For use in toggleOpen function. Used so height of content can be set in rem and not px.
  convertPixelsToRem(px: number) {
    return px / parseFloat(getComputedStyle(document.documentElement).fontSize);
  }

  toggleOpen() {
    this.isOpen = !this.isOpen;
    this.updateComplete.then(() => {
      const contentElement: HTMLElement | null | undefined =
        this.shadowRoot?.querySelector('.emergency-alert-content');
      const bodySlot = this.querySelector('[slot="body"]');
      const ctaSlot = this.querySelector('[slot="cta"]');
      if (this.isOpen) {
        const height = contentElement?.scrollHeight;
        if (height) {
          // Sets height (plus padding) in rem
          contentElement.style.setProperty(
            'max-height',
            `${this.convertPixelsToRem(height) + 3}rem`
          );
        }
        // Remove 'inert' when content is open
        if (bodySlot) bodySlot.removeAttribute('inert');
        if (ctaSlot) ctaSlot.removeAttribute('inert');
      } else {
        if (contentElement)
          contentElement.style.setProperty('max-height', '0px');
        if (bodySlot) bodySlot.setAttribute('inert', '');
        if (ctaSlot) ctaSlot.setAttribute('inert', '');
      }
    });
  }

  render(): TemplateResult {
    return html`
      <button
        class="emergency-alert-button"
        @click=${this.toggleOpen}
        aria-expanded=${this.isOpen ? 'true' : 'false'}
        aria-controls="emergency-alert-content"
        style="background-color: var(--${this.backgroundColor});"
      >
        <div class="title-container">
          <outline-icon-baseline
            name="${this.icon}"
            size="1.5rem"
          ></outline-icon-baseline>
          <slot name="title"></slot>
        </div>
        <outline-icon-baseline
          class="toggle-icon"
          name="${this.isOpen ? 'eu-minus' : 'eu-plus'}"
          size="1.5rem"
        ></outline-icon-baseline>
      </button>
      <div
        id="emergency-alert-content"
        class="emergency-alert-content ${this.isOpen ? 'open' : ''}"
        aria-hidden="${!this.isOpen ? 'true' : 'false'}"
        style="background-color: var(--${this.backgroundColor});"
      >
        ${this.slots.conditionalSlot('body', false)}
        ${this.slots.conditionalSlot('cta', false)}
      </div>
    `;
  }
}
